// How long the first index of this workbook will take, roughly, so the user can decide whether now is the
// moment. Pure: the caller measures the workbook (`measureWorkbook`, two syncs and no cell reads).
//
// The shape of the cost, from the runs that are on record:
//
//   · Solarion, 19 sheets / 645k cells — capture 70 s, commit 26 s, understanding 3 min 09 s → 281 s
//   · Wind farm, 13 sheets / ~280k cells — capture and commit around 40 s, understanding the larger half
//
// Two terms, because the two halves scale differently. Capture and commit follow the CELLS: the add-in
// reads them out of Excel and the server rebuilds the structures from them. The model pass follows the
// SHEETS: it is one call per sheet whatever the sheet holds, and on any real model it is the larger half.
// A flat few seconds covers the round trips that do not scale with either.
//
// It is presented as a range, and deliberately a wide one. The spread between a dense sheet and a sparse
// one of the same size is large (one Solarion sheet alone took 53 s of the 70), so a single number would
// be precise about something that is not.

/** Seconds of capture and commit per cell. */
const SECONDS_PER_CELL = 1 / 7000;
/** Seconds of the model pass per sheet — one call each, and the bigger half of the wait. */
const SECONDS_PER_SHEET = 9;
/** Round trips that scale with neither. */
const BASE_SECONDS = 8;

export interface WorkbookSize {
  sheets: number;
  cells: number;
}

export function estimateIndexSeconds({ sheets, cells }: WorkbookSize): number {
  if (sheets <= 0) return 0;
  return BASE_SECONDS + Math.max(0, cells) * SECONDS_PER_CELL + sheets * SECONDS_PER_SHEET;
}

export interface EstimateBand {
  /** i18n key under `intro.` for the phrasing. */
  key: 'under1' | 'about1' | 'range';
  low: number;
  high: number;
}

/**
 * The estimate as the band to say out loud: "less than a minute", "about a minute", or "2 to 4 minutes".
 * The range is −30% / +50%, which is the honest spread of the runs above rather than a flourish.
 */
export function estimateBand(size: WorkbookSize): EstimateBand {
  const seconds = estimateIndexSeconds(size);
  if (seconds < 55) return { key: 'under1', low: 0, high: 1 };
  const low = Math.max(1, Math.round((seconds * 0.7) / 60));
  const high = Math.max(low + 1, Math.round((seconds * 1.5) / 60));
  if (low === 1 && high <= 2) return { key: 'about1', low: 1, high: 2 };
  return { key: 'range', low, high };
}


// ── one bar for the whole run ────────────────────────────────────────────────
// Reading the sheets and understanding them are two phases with two counts, and each used to fill a bar of
// its own: it ran to the end, snapped back to zero and ran again. They are one wait, so they are one bar.
//
// The share each phase gets is the share of the time it is expected to take, from the same two terms as the
// estimate above — so the bar and the "about 2 to 4 minutes" beside it can never tell different stories. On
// a big workbook the model pass is most of the wait and gets most of the bar; on a tiny one it is the
// reading that dominates.

/** How much of the bar belongs to reading and committing, the rest being the model pass. */
export function captureShare(size: WorkbookSize | null): number {
  if (!size || size.sheets <= 0) return 0.5;
  const capture = BASE_SECONDS + Math.max(0, size.cells) * SECONDS_PER_CELL;
  const total = capture + size.sheets * SECONDS_PER_SHEET;
  return total > 0 ? Math.min(0.9, Math.max(0.1, capture / total)) : 0.5;
}

export interface RunProgress {
  /** 0 → 1 over the whole run, or null while nothing countable is happening yet. */
  fraction: number | null;
  /** What to count out loud beside the bar, when there is something to count. */
  done: number;
  total: number;
}

/** Where the run is, as one number. Monotonic across the hand-over: the reading's share is banked before
 *  the model pass starts adding to it, so the bar never walks backwards. */
export function runProgress(
  state: { phase: string; done: number; total: number; status?: { model_pass?: { done: number; total: number } } | null },
  size: WorkbookSize | null,
): RunProgress {
  const share = captureShare(size);
  const mp = state.status?.model_pass;
  switch (state.phase) {
    case 'probing':
    case 'idle':
    case 'needs_consent':
      return { fraction: null, done: 0, total: 0 };
    case 'capturing': {
      // The commit is the tail of the reading's share: it has no count of its own, so the bar stops a
      // little short of the hand-over rather than pretending the sheets are already in.
      const of = state.total > 0 ? state.done / state.total : 0;
      return { fraction: share * 0.85 * of, done: state.done, total: state.total };
    }
    case 'committing':
      return { fraction: share * 0.92, done: state.total, total: state.total };
    case 'understanding': {
      const of = mp && mp.total > 0 ? mp.done / mp.total : 0;
      return { fraction: share + (1 - share) * of, done: mp?.done ?? 0, total: mp?.total ?? 0 };
    }
    default:
      return { fraction: 1, done: state.total, total: state.total };
  }
}
